import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import type { Dependencies } from "@redux/store";
import type { AppState } from "@redux/app-state";
import type { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { setAccessTokenCookie } from "@utils/access-token-cookie";
import { writeActiveRoleToStorage } from "@shared-kernel/core/active-role/active-role";
import { markImpersonationStarted } from "@shared-kernel/core/impersonation/impersonation-marker";
import { syncSession } from "../session/sync-session";

export const impersonateUser = createAsyncThunk<
  { activeRole: ROLES },
  { targetUserId: string; activeRole: ROLES },
  { state: AppState; extra: Partial<Dependencies> }
>("admin/impersonateUser", async ({ targetUserId, activeRole }, { extra: { adminRepository }, dispatch }) => {
  // Only the access token is swapped: the admin's refresh token stays in its
  // cookie, so ending impersonation can resume the admin session from it (see
  // returnToAdminSession). The impersonation token itself has no refresh token.
  const { accessToken, expiresIn } = await adminRepository!.impersonate(targetUserId, activeRole);
  setAccessTokenCookie(accessToken, expiresIn);
  // Outlives the access token on purpose: it is what tells the next sync that a
  // session without an `originalAdminId` claim is an impersonation that expired.
  markImpersonationStarted();
  writeActiveRoleToStorage(activeRole);
  await dispatch(syncSession());
  return { activeRole };
});

export const stopImpersonation = createAction("admin/stopImpersonation");

export const resetUserImpersonation = createAction("admin/resetUserImpersonation");
